import { CruisePurchaseModel } from 'api-hooks/order/cruise';
import { CruiseSaleModel } from 'api-hooks/sale/cruise';
import { TourModel } from 'api-hooks/tour/tour-list/tour-detail';
import { generateId } from 'common/utils/string';
import { guestNameTransformer } from 'legacy-modules/sales/ticket-sale/hooks/use-get-default-values';
import React from 'react';

import {
  CruisePurchaseAddOnsItemFormType,
  CruisePurchaseDetailsItemFormType,
  CruisePurchaseFormType,
  CruisePurchaseGuestItemFormType,
  CruisePurchaseItemFormType,
} from '../components/form-type';

interface Props {
  cruiseSale?: CruiseSaleModel;
  cruisePurchase?: CruisePurchaseModel;
  defaultValues?: CruisePurchaseFormType;
}

export function addNewAddOns(): CruisePurchaseAddOnsItemFormType {
  return {
    id: undefined,
    customId: generateId(),
    description: '',
    guestId: '',
    guestName: '',
    price: undefined,
  } as never as CruisePurchaseAddOnsItemFormType;
}

export function addNewGuest(): CruisePurchaseGuestItemFormType {
  return {
    customId: generateId(),
    guestId: '',
    guestName: '',
    id: undefined,
    guestDetailId: '',
  } as never as CruisePurchaseGuestItemFormType;
}

export function addNewDetails(): CruisePurchaseDetailsItemFormType {
  return {
    customId: generateId(),
    guestId: '',
    guestName: '',
    cruiseRoomId: '',
    cruiseRoomName: '',
    cruiseRoomNumber: '',
    deck: '',
    description: '',
    basicFare: undefined,
    id: undefined,
    portCharge: undefined,
    tax: undefined,
    tipping: undefined,
    guestDetailId: '',
  } as never as CruisePurchaseDetailsItemFormType;
}

export function addNewPurchase(): CruisePurchaseItemFormType {
  return {
    id: undefined,
    customId: generateId(),
    cruisePurchaseAddOns: [],
    cruisePurchaseGuests: [addNewGuest()],
    cruisePurchaseDetails: [addNewDetails()],
    isGroupedPrice: true,
    bookingCode: '',
    cruiseId: '',
    cruiseName: '',
    cruiseShipId: '',
    cruiseShipName: '',
    reminder: false,
    route: '',
    startedAt: new Date(),
    endedAt: new Date(),
  } as never as CruisePurchaseItemFormType;
}

export function tourListToCruisePurchase(tourList: TourModel) {
  const transactionAt = new Date();

  const defaultValues: CruisePurchaseFormType = {
    orderNumber: '',
    saleTotal: 0,
    agentId: '',
    agentStatus: '',
    remainingTransactionQuota: null,
    isExceedingQuota: false,
    code: '',
    commission: 0,
    description: '',
    discount: 0,
    invoiceId: '',
    invoiceRefId: '',
    needApproval: false,
    otherCost: 0,
    paymentChannelId: '',
    refId: undefined,
    parentRefId: '',
    tempTransactionAt: transactionAt,
    transactionAt,
    tourId: tourList.id,
    tourRefId: tourList.refId,
    type: 'tour',
    cruiseSale: undefined,
    cruisePurchase: undefined,
    total: 0,
    cruisePurchases: [addNewPurchase()], //activity purchases will be fill with select sales
  };

  return defaultValues;
}

export default function useGetDefaultValues(props: Props) {
  const { cruisePurchase, cruiseSale } = props;
  const defaultValues = React.useMemo<CruisePurchaseFormType>(() => {
    const transactionAt = cruisePurchase?.transactionAt || new Date();
    if (props.defaultValues) return props.defaultValues;
    if (cruiseSale) {
      return {
        orderNumber: '',
        saleTotal: cruiseSale.total,
        agentId: '',
        agentStatus: '',
        remainingTransactionQuota: null,
        isExceedingQuota: false,
        code: cruiseSale.code || '',
        commission: 0,
        description: '',
        discount: cruiseSale.discount || 0,
        invoiceId: cruiseSale.id,
        invoiceRefId: cruiseSale.refId,
        needApproval: false,
        otherCost: cruiseSale.otherCost || 0,
        paymentChannelId: '',
        refId: undefined,
        parentRefId: '',
        tempTransactionAt: transactionAt,
        transactionAt,
        tourId: undefined,
        tourRefId: undefined,
        type: 'sales',
        cruiseSale,
        cruisePurchase,
        total: 0,
        cruisePurchases: [addNewPurchase()], //activity purchases will be fill with select sales
      };
    }
    const agent = cruisePurchase?.agent;
    const paymentChannel = cruisePurchase?.paymentChannel;
    const sale = cruisePurchase?.sale;
    const tour = cruisePurchase?.tour;

    const cruisePurchases: CruisePurchaseItemFormType[] =
      cruisePurchase?.cruisePurchases?.map((cp) => {
        const cruise = cp.cruise;
        const cruisePurchaseAddOns: CruisePurchaseAddOnsItemFormType[] =
          cp.cruisePurchaseAddOns.map((cpao) => {
            const guest = cpao.guest;
            return {
              customId: generateId(),
              description: cpao.description || '',
              guestId: guest.id,
              guestName: guestNameTransformer(guest),
              id: cpao.id,
              price: cpao.price,
            };
          });

        const cruisePurchaseGuests: CruisePurchaseGuestItemFormType[] =
          cp.cruisePurchaseGuests.map((cpg) => {
            const guest = cpg.guest;

            return {
              customId: generateId(),
              guestId: guest.id,
              guestName: guestNameTransformer(guest),
              id: cpg.id,
              guestDetailId: guest.id,
            };
          });

        const cruisePurchaseDetails: CruisePurchaseDetailsItemFormType[] =
          cp.cruisePurchaseDetails.map((cpd) => {
            const guest = cp.cruisePurchaseGuests.find(
              (cpg) => cpg.id === cpd.cruisePurchaseGuestId,
            )?.guest;
            const cruiseRoom = cpd.cruiseRoom;
            return {
              basicFare: cpd.basicFare,
              cruiseRoomId: cruiseRoom.id,
              cruiseRoomName: `${cruiseRoom.type} (${cruiseRoom.size})`,
              cruiseRoomNumber: cpd.cruiseRoomNumber,
              customId: generateId(),
              deck: cpd.deck || '',
              description: cpd.description || '',
              guestId: guest?.id || '',
              guestName: !cp.isGroupedPrice ? guestNameTransformer(guest!) : '',
              id: cpd.id,
              portCharge: cpd.portCharge,
              tax: cpd.tax,
              tipping: cpd.tipping,
              guestDetailId: guest?.id || '',
            };
          });

        return {
          bookingCode: cp.bookingCode || '',
          cruiseId: cruise.id,
          cruiseName: cruise.name,
          cruiseShipId: cp.cruiseShip?.id || '',
          cruiseShipName: cp.cruiseShip?.name || '',
          customId: generateId(),
          endedAt: cp.endedAt,
          startedAt: cp.startedAt,
          id: cp.id,
          isGroupedPrice: cp.isGroupedPrice,
          reminder: cp.reminder,
          route: cp.route,
          cruisePurchaseAddOns,
          cruisePurchaseGuests,
          cruisePurchaseDetails,
        };
      }) ?? [addNewPurchase()];

    return {
      agentId: agent?.id || '',
      agentStatus: agent?.status || '',
      remainingTransactionQuota: null,
      isExceedingQuota: false,
      paymentChannelId: paymentChannel?.id || '',
      code: cruisePurchase?.code || '',
      orderNumber: cruisePurchase?.number || '',
      commission: cruisePurchase?.agentCommission?.amount || 0,
      description: cruisePurchase?.description || '',
      discount: cruisePurchase?.discount || 0,
      refId: cruisePurchase?.refId,
      parentRefId: cruisePurchase?.parentPurchase?.id,
      invoiceId: sale?.id,
      tourId: tour?.id,
      tourRefId: cruisePurchase?.tour?.refId,
      invoiceRefId: cruisePurchase?.invoiceRefId,
      needApproval: false,
      transactionAt,
      tempTransactionAt: transactionAt,
      otherCost: cruisePurchase?.otherCost || 0,
      total: cruisePurchase?.total || 0,
      saleTotal: sale?.total || 0,
      type: cruisePurchase?.type || 'sales',
      cruisePurchases,

      cruisePurchase,
      cruiseSale,
    };
  }, [cruisePurchase, cruiseSale, props.defaultValues]);

  return defaultValues;
}
